import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CatalogItemDetails,
  CreateCatalogItemFromProjectItemRequest,
  CreateCatalogItemFromProjectItemResponse,
  CreateCatalogItemRequest,
  ListCatalogItemsQuery,
  UpdateBomOfferNameRequest,
  UpdateCatalogItemRequest,
} from '@kreart/shared';
import { projectsQueryKeys } from '@/features/projects/hooks';
import { catalogApi } from './api';

const ROOT = ['catalog-items'] as const;
export const catalogQueryKeys = {
  all: ROOT,
  list: (q: ListCatalogItemsQuery) => [...ROOT, 'list', q] as const,
  detail: (id: string) => [...ROOT, 'detail', id] as const,
};

export function useCatalogItems(query: ListCatalogItemsQuery) {
  return useQuery({
    queryKey: catalogQueryKeys.list(query),
    queryFn: () => catalogApi.list(query),
    placeholderData: keepPreviousData,
    meta: { silent: true },
  });
}

export function useCatalogItem(id: string | undefined) {
  return useQuery({
    queryKey: id ? catalogQueryKeys.detail(id) : ['catalog-items', 'detail', 'nil'],
    queryFn: () => catalogApi.get(id!),
    enabled: Boolean(id),
    meta: { silent: true },
  });
}

export function useCreateCatalogItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCatalogItemRequest) => catalogApi.create(body),
    meta: { silent: true },
    onSuccess: (data: CatalogItemDetails) => {
      qc.setQueryData(catalogQueryKeys.detail(data.id), data);
      void qc.invalidateQueries({ queryKey: [...ROOT, 'list'] });
    },
  });
}

export function useUpdateCatalogItem(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateCatalogItemRequest) => catalogApi.update(id, body),
    meta: { silent: true },
    onSuccess: (data: CatalogItemDetails) => {
      qc.setQueryData(catalogQueryKeys.detail(id), data);
      void qc.invalidateQueries({ queryKey: [...ROOT, 'list'] });
    },
  });
}

export function useDeleteCatalogItem(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => catalogApi.remove(id),
    meta: { silent: true },
    onSuccess: () => {
      qc.removeQueries({ queryKey: catalogQueryKeys.detail(id) });
      void qc.invalidateQueries({ queryKey: [...ROOT, 'list'] });
    },
  });
}

const PROJECT_ITEM_QUERY_ROOT: Record<
  CreateCatalogItemFromProjectItemRequest['source'],
  readonly string[]
> = {
  cabinet: ['project-cabinets'],
  panel: ['project-panels'],
  countertop: ['project-countertops'],
  element: ['project-elements'],
  lighting: ['project-lighting'],
  drawer: ['project-drawers'],
};

export function usePromoteFromProjectItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCatalogItemFromProjectItemRequest) =>
      catalogApi.promoteFromProjectItem(body),
    meta: { silent: true },
    onSuccess: (data: CreateCatalogItemFromProjectItemResponse, vars) => {
      qc.setQueryData(catalogQueryKeys.detail(data.catalogItem.id), data.catalogItem);
      void qc.invalidateQueries({ queryKey: [...ROOT, 'list'] });
      if (data.addedToLibrary) {
        void qc.invalidateQueries({ queryKey: ['library'] });
      }
      void qc.invalidateQueries({ queryKey: PROJECT_ITEM_QUERY_ROOT[vars.source] });
    },
  });
}

/**
 * Inline BOM "Offer Name" edit — patches the catalog entity behind a BOM row.
 * Prefix-invalidates every scoped BOM view for the project so the label refreshes.
 */
export function useUpdateBomOfferName(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateBomOfferNameRequest) => catalogApi.updateOfferName(body),
    meta: { silent: true },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectsQueryKeys.bom(projectId) });
      void qc.invalidateQueries({ queryKey: [...ROOT, 'list'] });
    },
  });
}
